import { ArrowDown, ArrowUp, Hourglass, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  ACTION_LABEL,
  ACTION_ORDER,
  DURATION_UNIT_LABEL,
  indentedName,
  NOTIFY_RECIPIENT_LABEL,
  TICKET_PLACEHOLDERS,
  defaultAction,
} from '../lib/automation';
import type { DurationUnit, NotifyRecipient, WorkflowAction, WorkflowActionType, WorkflowListItem, WorkOrderPriority, WorkOrderStatus } from '../lib/types';
import { WORK_ORDER_PRIORITY_LABEL, WORK_ORDER_STATUS_LABEL } from '../lib/work-orders';
import { ChipSelect, type ConditionOptions } from './ConditionBuilder';
import { cn, Field, Input, Select, Textarea } from './ui';

function Check({ checked, onChange, children }: { checked: boolean; onChange(checked: boolean): void; children: ReactNode }) {
  return (
    <label className="flex items-center gap-2 text-sm text-slate-700">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="size-4 rounded border-slate-300" />
      {children}
    </label>
  );
}

export function Placeholders({ names }: { names: string[] }) {
  return (
    <p className="text-xs leading-5 text-slate-500">
      You can use{' '}
      {names.map((name, index) => (
        <span key={name}>
          <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[11px] text-slate-700">{`{{${name}}}`}</code>
          {index < names.length - 1 ? ' ' : ''}
        </span>
      ))}
    </p>
  );
}

function DepartmentSelect({ value, onChange, options, placeholder = 'Choose a department' }: { value: string; onChange(id: string): void; options: ConditionOptions; placeholder?: string }) {
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {options.departments.map((department) => (
        <option key={department.id} value={department.id}>
          {indentedName(department)}
        </option>
      ))}
    </Select>
  );
}

function UserSelect({ value, onChange, options }: { value: string; onChange(id: string): void; options: ConditionOptions }) {
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Choose a person</option>
      {options.users.map((user) => (
        <option key={user.id} value={user.id}>
          {user.name}
        </option>
      ))}
    </Select>
  );
}

function ActionFields({
  action,
  onChange,
  options,
  workflows,
}: {
  action: WorkflowAction;
  onChange(action: WorkflowAction): void;
  options: ConditionOptions;
  workflows: WorkflowListItem[];
}) {
  const users = options.users.map((u) => ({ value: u.id, label: u.name }));

  switch (action.type) {
    case 'assign':
      return (
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Assign to">
              <Select value={action.mode} onChange={(e) => onChange({ ...action, mode: e.target.value as typeof action.mode })}>
                <option value="round_robin">The next person in rotation (round-robin)</option>
                <option value="department_lead">The department’s lead</option>
                <option value="user">A specific person</option>
                <option value="unassign">Nobody (unassign)</option>
              </Select>
            </Field>
            {action.mode === 'user' && (
              <Field label="Person">
                <UserSelect value={action.userId ?? ''} onChange={(userId) => onChange({ ...action, userId })} options={options} />
              </Field>
            )}
            {(action.mode === 'round_robin' || action.mode === 'department_lead') && (
              <Field label="From">
                <Select value={action.pool} onChange={(e) => onChange({ ...action, pool: e.target.value as typeof action.pool })}>
                  <option value="ticket">The ticket’s department</option>
                  <option value="department">A chosen department</option>
                  {action.mode === 'round_robin' && <option value="everyone">The whole technology team</option>}
                </Select>
              </Field>
            )}
          </div>
          {(action.mode === 'round_robin' || action.mode === 'department_lead') && action.pool === 'department' && (
            <Field label="Department">
              <DepartmentSelect value={action.departmentId ?? ''} onChange={(departmentId) => onChange({ ...action, departmentId })} options={options} />
            </Field>
          )}
          {action.mode === 'round_robin' && (
            <p className="text-xs text-slate-500">
              People who are away are skipped. If nobody in the department is available, the parent department’s rotation is used. With no department,
              it rotates through the whole team.
            </p>
          )}
          {action.mode !== 'unassign' && (
            <Check checked={action.onlyIfUnassigned} onChange={(onlyIfUnassigned) => onChange({ ...action, onlyIfUnassigned })}>
              Only if nobody is assigned yet
            </Check>
          )}
        </div>
      );

    case 'route':
      return (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Department">
            <DepartmentSelect value={action.departmentId} onChange={(departmentId) => onChange({ ...action, departmentId })} options={options} />
          </Field>
          <Field label="Assignee">
            <Select value={action.assign} onChange={(e) => onChange({ ...action, assign: e.target.value as typeof action.assign })}>
              <option value="round_robin">Assign round-robin in that department</option>
              <option value="department_lead">Assign to that department’s lead</option>
              <option value="keep">Keep the current assignee</option>
            </Select>
          </Field>
        </div>
      );

    case 'escalate':
      return (
        <div className="space-y-3">
          <Check checked={action.raisePriority} onChange={(raisePriority) => onChange({ ...action, raisePriority })}>
            Raise the priority one level
          </Check>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Hand it to">
              <Select value={action.reassign} onChange={(e) => onChange({ ...action, reassign: e.target.value as typeof action.reassign })}>
                <option value="department_lead">The department’s lead</option>
                <option value="parent_department">The parent department (and its lead)</option>
                <option value="user">A specific person</option>
                <option value="keep">Keep the current assignee</option>
              </Select>
            </Field>
            {action.reassign === 'user' && (
              <Field label="Person">
                <UserSelect value={action.userId ?? ''} onChange={(userId) => onChange({ ...action, userId })} options={options} />
              </Field>
            )}
          </div>
          <Field label="Internal note" hint="Optional">
            <Textarea rows={2} maxLength={2000} value={action.note ?? ''} onChange={(e) => onChange({ ...action, note: e.target.value })} />
          </Field>
        </div>
      );

    case 'set_status':
      return (
        <Field label="New status">
          <Select value={action.status} onChange={(e) => onChange({ ...action, status: e.target.value as WorkOrderStatus })} className="sm:w-60">
            {(Object.keys(WORK_ORDER_STATUS_LABEL) as WorkOrderStatus[]).map((status) => (
              <option key={status} value={status}>
                {WORK_ORDER_STATUS_LABEL[status]}
              </option>
            ))}
          </Select>
        </Field>
      );

    case 'set_priority':
      return (
        <Field label="New priority">
          <Select value={action.priority} onChange={(e) => onChange({ ...action, priority: e.target.value as WorkOrderPriority })} className="sm:w-60">
            {(Object.keys(WORK_ORDER_PRIORITY_LABEL) as WorkOrderPriority[]).map((priority) => (
              <option key={priority} value={priority}>
                {WORK_ORDER_PRIORITY_LABEL[priority]}
              </option>
            ))}
          </Select>
        </Field>
      );

    case 'watch':
      return (
        <div className="space-y-3">
          <Check checked={action.departmentLeads} onChange={(departmentLeads) => onChange({ ...action, departmentLeads })}>
            The ticket department’s leads
          </Check>
          <div>
            <span className="mb-1 block text-sm font-medium text-slate-700">People</span>
            <ChipSelect options={users} value={action.userIds} onChange={(userIds) => onChange({ ...action, userIds })} placeholder="Add a person" />
          </div>
        </div>
      );

    case 'notify':
      return (
        <div className="space-y-3">
          <div>
            <span className="mb-1 block text-sm font-medium text-slate-700">Who</span>
            <div className="grid gap-1.5 sm:grid-cols-2">
              {(Object.keys(NOTIFY_RECIPIENT_LABEL) as NotifyRecipient[]).map((recipient) => (
                <Check
                  key={recipient}
                  checked={action.recipients.includes(recipient)}
                  onChange={(checked) =>
                    onChange({
                      ...action,
                      recipients: checked ? [...action.recipients, recipient] : action.recipients.filter((r) => r !== recipient),
                    })
                  }
                >
                  {NOTIFY_RECIPIENT_LABEL[recipient]}
                </Check>
              ))}
            </div>
          </div>
          <div>
            <span className="mb-1 block text-sm font-medium text-slate-700">Also notify</span>
            <ChipSelect options={users} value={action.userIds} onChange={(userIds) => onChange({ ...action, userIds })} placeholder="Add a person" />
          </div>
          <Field label="Title" required>
            <Input value={action.title} maxLength={200} onChange={(e) => onChange({ ...action, title: e.target.value })} />
          </Field>
          <Field label="Message">
            <Textarea rows={2} maxLength={2000} value={action.message} onChange={(e) => onChange({ ...action, message: e.target.value })} />
          </Field>
          <Placeholders names={TICKET_PLACEHOLDERS} />
        </div>
      );

    case 'internal_note':
      return (
        <div className="space-y-2">
          <Textarea rows={3} maxLength={5000} value={action.body} onChange={(e) => onChange({ ...action, body: e.target.value })} placeholder="Visible to staff only" />
          <Placeholders names={TICKET_PLACEHOLDERS} />
        </div>
      );

    case 'wait':
      return (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
            <span>Wait</span>
            <Input
              type="number"
              min={1}
              max={10000}
              value={String(action.amount)}
              onChange={(e) => onChange({ ...action, amount: Number(e.target.value) || 1 })}
              className="w-20"
              aria-label="Amount"
            />
            <Select value={action.unit} onChange={(e) => onChange({ ...action, unit: e.target.value as DurationUnit })} className="w-auto" aria-label="Unit">
              {(Object.keys(DURATION_UNIT_LABEL) as DurationUnit[]).map((unit) => (
                <option key={unit} value={unit}>
                  {DURATION_UNIT_LABEL[unit]}
                </option>
              ))}
            </Select>
            <span>then run the steps below.</span>
          </div>
          <Check checked={action.businessHours} onChange={(businessHours) => onChange({ ...action, businessHours })}>
            Count only business hours (skips nights, weekends, and holidays)
          </Check>
          <Check checked={action.recheck} onChange={(recheck) => onChange({ ...action, recheck })}>
            Stop if the ticket no longer matches the conditions
          </Check>
        </div>
      );

    case 'run_workflow':
      return (
        <Field label="Workflow" hint="Its conditions are checked against the ticket first. It can be turned off and still run from here.">
          <Select value={action.workflowId} onChange={(e) => onChange({ ...action, workflowId: e.target.value })}>
            <option value="">Choose a workflow</option>
            {workflows.map((workflow) => (
              <option key={workflow.id} value={workflow.id}>
                {workflow.name}
                {workflow.enabled ? '' : ' (off)'}
              </option>
            ))}
          </Select>
        </Field>
      );
  }
}

/** Ordered workflow actions, including waits that pause before the next step. */
export function ActionsEditor({
  actions,
  onChange,
  options,
  workflows,
}: {
  actions: WorkflowAction[];
  onChange(actions: WorkflowAction[]): void;
  options: ConditionOptions;
  /** Other workflows that can be chained. */
  workflows: WorkflowListItem[];
}) {
  const move = (index: number, by: number) => {
    const next = [...actions];
    const [item] = next.splice(index, 1);
    next.splice(index + by, 0, item);
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {actions.length === 0 && <p className="text-sm text-slate-500">No actions yet.</p>}
      <ol className="space-y-3">
        {actions.map((action, index) => (
          <li
            key={index}
            className={cn('rounded-lg border', action.type === 'wait' ? 'border-dashed border-amber-300 bg-amber-50/50' : 'border-slate-200 bg-white')}
          >
            <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
              <span
                className={cn(
                  'flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                  action.type === 'wait' ? 'bg-amber-100 text-amber-800' : 'bg-brand-100 text-brand-800',
                )}
              >
                {action.type === 'wait' ? <Hourglass className="size-3.5" /> : index + 1}
              </span>
              <span className="text-sm font-semibold text-slate-800">{ACTION_LABEL[action.type]}</span>
              <div className="ml-auto flex items-center">
                <button type="button" disabled={index === 0} onClick={() => move(index, -1)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30" aria-label="Move up">
                  <ArrowUp className="size-4" />
                </button>
                <button
                  type="button"
                  disabled={index === actions.length - 1}
                  onClick={() => move(index, 1)}
                  className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
                  aria-label="Move down"
                >
                  <ArrowDown className="size-4" />
                </button>
                <button type="button" onClick={() => onChange(actions.filter((_, i) => i !== index))} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-red-600" aria-label="Remove action">
                  <Trash2 className="size-4" />
                </button>
              </div>
            </div>
            <div className="p-3">
              <ActionFields action={action} onChange={(next) => onChange(actions.map((a, i) => (i === index ? next : a)))} options={options} workflows={workflows} />
            </div>
          </li>
        ))}
      </ol>
      <Select
        value=""
        onChange={(e) => e.target.value && onChange([...actions, defaultAction(e.target.value as WorkflowActionType)])}
        className="w-full sm:w-72"
        aria-label="Add an action"
      >
        <option value="">+ Add an action…</option>
        {ACTION_ORDER.map((type) => (
          <option key={type} value={type}>
            {ACTION_LABEL[type]}
          </option>
        ))}
      </Select>
    </div>
  );
}
